import CommonMapper from '@/modules/core/mappers/CommonMapper'
import Insurance from '@/modules/common/common/models/APP/Insurance'
import ProcessStatus from '@/modules/common/hiring/models/APP/ProcessStatus'
import ForeignHealthHiringAnswers from '@/modules/foreign-health/hiring/models/APP/ForeignHealthHiringAnswers'
import type ForeignHealthGetRecoveryResponse from '@/modules/foreign-health/hiring/models/MID/responses/ForeignHealthGetRecoveryResponse'
import type ForeignHealthInsurance from '@/modules/foreign-health/common/models/APP/ForeignHealthInsurance'
import ForeignHealthInsured from '../../models/MID/ForeignHealthInsured'
import { UserRoleEnum } from '@/modules/core/models/enums'
import DocumentDetails from '@/modules/common/hiring/models/APP/DocumentDetails'
import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
import { EXTENSION_MIMETYPE } from '@/modules/common/common/constants/CommonConstants'
import type ForeignHealthConfigurations from '@/modules/foreign-health/common/models/APP/ForeignHealthConfigurations'

function mapToInsured(data: any, role: UserRoleEnum, isStudent: boolean, files?: any) {
  const file = files?.find((f: any) => f.documentType === '1')
  const fileExtra = files?.find((f: any) => f.documentType !== '1')
  const dataProtection = data.dataProtection

  const insured: Partial<ForeignHealthInsured> = {
    riskNumber: data.riskNumber,
    role: role,
    documentDetails: _getDocumentDetails(data, file),
    extraDocument: _getDocumentDetails(data, fileExtra),
    addressDetails: new AddressDetails({
      postalCode: data.postalCode,
      province: data.province,
      city: data.city,
      address: data.street,
      country: data.nationality,
      houseType: data.houseType ? String(data.houseType) : undefined,
      gateway: data.gateway,
      stair: data.stair,
      floor: data.floor,
      doorway: data.doorway,
      addressExtra: data.restAddress,
      streetType: data.streetType,
      roadNumber: data.house
    }),
    userDetails: new UserDetails({
      birthDate: data.birthDate ? CommonMapper.fromMiddlewareDate(data.birthDate) : undefined,
      name: data.name?.toUpperCase(),
      surname1: data.firstSurname?.toUpperCase(),
      surname2: data.secondSurname?.toUpperCase(),
      gender: data.sex, // H / M
      email: data.email,
      phone: data.mobilePhone ? String(data.mobilePhone) : undefined,
      prefix: data.prefix
    }),
    privacyPolicyConsent: dataProtection?.privacyPolicyConsent,
    asisaNewsConsent: dataProtection?.asisaNewsConsent,
    thirdPartyWrittenAdvertising: dataProtection?.thirdPartyTelephoneAdvertising,
    thirdPartyTelephoneAdvertising: dataProtection?.thirdPartyWrittenAdvertising,
    dataTransfer: dataProtection?.dataTransfer,
    studentSituation: data.student ?? (isStudent ? 'S' : null),
    additionalCoverages:
      data.coverages?.map((a: any) => ({
        key: a.coverageCode,
        value: a.sumInsured,
        checked: true
      })) ?? []
  }

  if (!['undefined', 'null'].includes(typeof data.isFirstInsured)) {
    insured.isFirstInsured = data.isFirstInsured
  }

  if (!['undefined', 'null'].includes(typeof data.sendPrecontractualDocuments)) {
    insured.preContractualDocumentChecked = data.sendPrecontractualDocuments
  }

  return new ForeignHealthInsured(insured)
}

function _getDocumentDetails(data: any, file: any) {
  return new DocumentDetails({
    personType: data?.personType,
    documentType: data?.documentType,
    documentNumber: data?.documentNumber,
    documentExpirationDate: data?.documentExpirationDate,
    documentId: file?.documentId || file?.documentData,
    fileName: file?.fileName,
    mimeType: file
      ? file?.mimeType
        ? EXTENSION_MIMETYPE[file.mimeType.toLowerCase()]
        : 'application/pdf'
      : undefined
  })
}

export default class {
  static fromResponse(
    data: ForeignHealthGetRecoveryResponse,
    product: ForeignHealthInsurance,
    config: ForeignHealthConfigurations,
    quotationNull: boolean = false
  ): ForeignHealthHiringAnswers {
    let file = data.documents.filter((d: any) => d.riskNumber === 0)
    const isStudent = data.hiring.stayAbroadType.toString() === config.insuranceTypes.student.id
    const holder = mapToInsured(
      data.policyHolder,
      data.policyHolder.role,
      isStudent && data.policyHolder.role === UserRoleEnum.HOLDER_INSURED,
      file
    )
    let isFirstOne = true

    const insureds =
      data.insureds.map((insured: any) => {
        const isHolder =
          data.policyHolder.role === UserRoleEnum.HOLDER_INSURED && insured.riskNumber === 1
        const documents = data.documents.filter((d: any) => d.riskNumber === insured.riskNumber)
        file = isHolder && file.length === 0 ? documents : file
        const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED
        const isFirstInsured = !isHolder && isFirstOne
        if (isFirstInsured) {
          isFirstOne = false
        }

        return mapToInsured({ ...insured, isFirstInsured }, role, isStudent, documents)
      }) ?? []

    if (holder.role === UserRoleEnum.HOLDER_INSURED) {
      const preContractualDocumentChecked = holder.preContractualDocumentChecked
      const thirdPartiesAnswered = holder.thirdPartiesAnswered
      const thirdPartyTelephoneAdvertising = holder.thirdPartyTelephoneAdvertising
      const thirdPartyWrittenAdvertising = holder.thirdPartyWrittenAdvertising
      const privacyPolicyConsent = holder.privacyPolicyConsent
      const dataTransfer = holder.dataTransfer

      insureds[0].updateAttributes({
        preContractualDocumentChecked,
        thirdPartiesAnswered,
        thirdPartyTelephoneAdvertising,
        thirdPartyWrittenAdvertising,
        privacyPolicyConsent,
        dataTransfer
      })
      insureds[0].userDetails.updateAttributes({
        email: holder.userDetails.email
      })
      holder.updateAttributes(insureds[0])
    }

    return new ForeignHealthHiringAnswers({
      quotationId: quotationNull ? null : data.quotationId,
      businessLine: data.hiring.businessLine,
      billingCycle: CommonMapper.fromDXPProductPrice(data.hiring.billingCycle),
      price: data.hiring.price,
      totalPrice: data.hiring.totalPrice,
      abroadOption: data.hiring.stayAbroadType,
      stayAbroadType: data.hiring.stayAbroadType,
      insuranceTypes: data.hiring.insuranceTypes,
      insuranceType: data.hiring.insuranceTypes![0],
      betterAssurance: insureds.some((insured) => insured.additionalCoverages.length),
      product: new Insurance({
        ...product,
        quotationNumber: quotationNull
          ? null
          : data.hiring.quotationNumber
          ? String(data.hiring.quotationNumber)
          : null,
        clientCode: String(data.hiring.clientCode),
        productCode: String(data.hiring.productCode)
      }),
      processStatus: new ProcessStatus({
        statusCode: data.hiring.statusCode,
        stepCode: data.hiring.stepCode
      }),
      startDate: data.hiring.insuranceStartDate
        ? CommonMapper.fromMiddlewareDate(data.hiring.insuranceStartDate)
        : undefined,
      endDate: data.hiring.insuranceEndDate
        ? CommonMapper.fromMiddlewareDate(data.hiring.insuranceEndDate)
        : undefined,
      insureds: insureds,
      holder
    })
  }

  static toRequest() {}
}
